/**
 * Which sprite sheets to fetch, and when. Only the two rosters' Pokémon are ever requested: the
 * fourteen on the field in the facing their side shows (offense from behind, defense from the
 * front) block the first play; everything else they could need later (the other facing after a
 * change of possession, the bench once it exists) loads in the background. An id with no sheet
 * gets no request and draws the placeholder. No Phaser import, so it can be tested in Vitest.
 */
import { DEFENSE_SLOTS, OFFENSE_SLOTS } from '@tdmons/sim';
import type { Combatant, GameState } from '@tdmons/sim';

export type Facing = 'front' | 'back';

/** `spriteIndex.json`, written by tools/sprites. */
export interface SpriteIndex {
  v: 1;
  /** Median Gen 1 sprite height in source pixels; the field scale is fitted to it. */
  refHeight: number;
  /** creatureId -> generation folder. */
  gens: Readonly<Record<string, number>>;
}

/** One facing of one Pokémon: its sheet JSON (shared by both facings) and its PNG. */
export interface SpriteRequest {
  id: string;
  facing: Facing;
  /** Texture key: `snorlax-back`. */
  key: string;
  /** JSON cache key: `sheet:snorlax`. */
  jsonKey: string;
  jsonUrl: string;
  pngUrl: string;
}

export interface SpritePlan {
  now: SpriteRequest[];
  later: SpriteRequest[];
}

/** The subset of Phaser's loader this module drives. */
export interface SheetLoader {
  json(key: string, url: string): unknown;
  image(key: string, url: string): unknown;
}

export const facingFor = (side: 'off' | 'def'): Facing => (side === 'off' ? 'back' : 'front');
export const textureKey = (id: string, facing: Facing) => `${id}-${facing}`;
export const jsonKey = (id: string) => `sheet:${id}`;

const DEFAULT_BASE = `${import.meta.env.BASE_URL}sprites`;

function request(id: string, gen: number, facing: Facing, base: string): SpriteRequest {
  const dir = `${base}/gen${gen}`;
  return {
    id,
    facing,
    key: textureKey(id, facing),
    jsonKey: jsonKey(id),
    jsonUrl: `${dir}/${id}.json`,
    pngUrl: `${dir}/${id}-${facing}.png`,
  };
}

export function spritePlan(
  state: GameState,
  index: SpriteIndex,
  base: string = DEFAULT_BASE,
): SpritePlan {
  const sides: [Combatant, 'off' | 'def'][] = [
    ...OFFENSE_SLOTS.map((s): [Combatant, 'off'] => [state.offense.lineup[s], 'off']),
    ...DEFENSE_SLOTS.map((s): [Combatant, 'def'] => [state.defense.lineup[s], 'def']),
  ];
  const seen = new Set<string>();
  // Each facing once, and a facing the field shows now is never left for later (a Pokémon
  // fielded on both sides needs both up front).
  const pass = (pick: (side: 'off' | 'def') => Facing): SpriteRequest[] => {
    const out: SpriteRequest[] = [];
    for (const [c, side] of sides) {
      const gen = index.gens[c.creatureId];
      const facing = pick(side);
      const key = textureKey(c.creatureId, facing);
      if (gen === undefined || seen.has(key)) continue;
      seen.add(key);
      out.push(request(c.creatureId, gen, facing, base));
    }
    return out;
  };
  const now = pass(facingFor);
  const later = pass((side) => (facingFor(side) === 'back' ? 'front' : 'back'));
  return { now, later };
}

/** Queue each sheet's JSON once and each facing's PNG; both go out together, no waterfall. */
export function queueSprites(loader: SheetLoader, requests: readonly SpriteRequest[]): void {
  const jsons = new Set<string>();
  for (const r of requests) {
    if (!jsons.has(r.jsonKey)) {
      jsons.add(r.jsonKey);
      loader.json(r.jsonKey, r.jsonUrl);
    }
    loader.image(r.key, r.pngUrl);
  }
}
